/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { jwtDecode } from "jwt-decode";

import logoLight from "../../assets/images/logo-light.png";
import logosmall from "../../assets/images/logo-small-screen.png";
import ParticlesAuth from "./ParticlesAuth";
import {api} from "../../config";

// ✅ Redux Actions
const loginSuccess = (user) => ({ type: "LOGIN_SUCCESS", payload: user });
const loginFailure = (message) => ({ type: "LOGIN_FAILURE", payload: message });
const enableLoading = () => ({ type: "ENABLE_LOADING" });

// ✅ Thunk: Login with token decoding and expiry check
const loginUser = (credentials, navigate) => {
  return async (dispatch) => {
    dispatch(enableLoading());
    try {
      const res = await fetch(`${api.API_URL}api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      const data = await res.json();
      console.log("Login API response:", data);

      if (res.ok && data.data?.token) {
        const token = data.data.token;
        const decodedToken = jwtDecode(token);

        const userWithToken = {
          token,
          user: decodedToken,
        };

        localStorage.setItem("authUser", JSON.stringify(userWithToken));

        dispatch(loginSuccess(userWithToken));

        // ✅ Navigate only after successful login
        navigate("/dashboard");
      } else {
        dispatch(loginFailure(data.message || "Invalid email or password"));
      }
    } catch (err) {
      console.error("Login error:", err);
      dispatch(loginFailure("Something went wrong"));
    }
  };
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { errorMsg, loading, user } = useSelector((state) => ({
    errorMsg: state.Login?.errorMsg,
    loading: state.Login?.loading,
    user: state.Login?.user,
  }));

  const [passwordShow, setPasswordShow] = useState(false);

  // ✅ Navigate after login success
  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const parsedUser = JSON.parse(authUser);
      const decodedToken = jwtDecode(parsedUser.token);
      const isTokenExpired = decodedToken.exp * 1000 < Date.now();
      navigate("/dashboard");
      if (!isTokenExpired) {
        dispatch(loginSuccess(parsedUser)); // Store user in redux
      } else {
        localStorage.removeItem("authUser"); // Clear expired token
      }
    }
  }, []);

  // ✅ Formik validation
  const validation = useFormik({
    initialValues: {  
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Please enter your email"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please enter your password"),
    }),
    onSubmit: (values) => {
      dispatch(loginUser(values, navigate)); // ⬅️ pass navigate
    },
  });

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
       <Row className="mt-4">
  <Col lg={12}>
    <div className="text-center mt-sm-5 mb-4 text-white-50 mt-3">
      <div className="mt-3">
        <Link to="/dashboard" className="d-inline-block auth-logo">
          <img
            src={logoLight}
            alt="logo"
            className="img-fluid mx-auto d-block"
            style={{ height: "100px" }} // Decreased size
          />
        </Link>
      </div>
    </div>
  </Col>
</Row>a


          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">Welcome Back!</h5>
                    <p className="text-muted">Sign in to continue to Screen Ai.</p>
                  </div>

                  {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

                  <div className="p-2 mt-4">
                    <Form onSubmit={validation.handleSubmit}>
                      <div className="mb-3">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Enter email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email}
                          invalid={validation.touched.email && !!validation.errors.email}
                        />
                        <FormFeedback>{validation.errors.email}</FormFeedback>
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="password">Password</Label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <Input
                            id="password"
                            name="password"
                            type={passwordShow ? "text" : "password"}
                            placeholder="Enter password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={validation.touched.password && !!validation.errors.password}
                          />
                          <FormFeedback>{validation.errors.password}</FormFeedback>
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-0"
                            onClick={() => setPasswordShow(!passwordShow)}
                          >
                            <i className="ri-eye-fill align-middle"></i>
                          </button>
                        </div>
                      </div>

                      <div className="form-check mb-3">
                        <Input className="form-check-input" type="checkbox" id="remember" />
                        <Label className="form-check-label" htmlFor="remember">
                          Remember me
                        </Label>
                      </div>

                      <div className="mt-4">
                        <Button color="primary" type="submit" disabled={loading} className="w-100">
                          {loading ? <Spinner size="sm" className="me-2" /> : "Sign In"}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default Login;
